import React, {useState} from 'react';
import {
    useDisableContactMutation,
    useEnableContactMutation,
    useGetContactsQuery,
    useGetInstalledExtensionsQuery,
} from '../../store/Api';
import {
    ICommandBarItemProps,
    IconButton,
    Link,
    Toggle
} from '@fluentui/react';
import {useHistory} from 'react-router-dom';
import { IContact } from '../../models/Company';
import ContactDetailsModal from './ContactDetailsModal';
import CompanyDetailsModal from './CompanyDetailsModal';
import { EXTENSION_TYPE_CUSTOMERIMPORT, IInstalledExtension } from '../../models/Extension';
import ContactImportPanel from './ContactImportPanel';
import CsvImportPanel from './CsvImportPanel';
import DisableEnableButton from "./DisableEnableButton";
import GridComposer from "../GridComposer/GridComposer";
import {GridSelectionMode, IColumnDetails} from "../../models/GridConfiguration";
import {useGetStorefrontsQuery} from '../../store/endpoints/StorefrontSettingsEndpoints';
import CompanyFilterMenu from './CompanyFilterMenu';
import {useUrlPanelState} from '../../hooks/useUrlState';

const ContactsManagementDisplay: React.FC = () => {
    const [query] = React.useState('');
    const [showDisabled, setShowDisabled] = React.useState(false);
    const extensions = useGetInstalledExtensionsQuery();
    const storefronts = useGetStorefrontsQuery();
    /*
     * Editing an existing contact goes through the URL (?panel=contact&instance=<id>) so a link can
     * open it; "New" stays local, having no contact to name yet. The modal wants the whole record,
     * so this resolves it out of the loaded page — a link to a contact on another page of the grid
     * opens the tab but not the modal. CompanyDetailsModal takes only an id and has no such limit.
     */
    const contactPanel = useUrlPanelState('contact');
    const [newContact, setNewContact] = React.useState(false);
    const [selectedCompany, setSelectedCompany] = React.useState<string | undefined>(undefined);
    const [selectedImportExtension, setSelectedImportExtension] = React.useState<IInstalledExtension | undefined>(undefined);
    const [showCsvImport, setShowCsvImport] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [disableContact] = useDisableContactMutation();
    const [enableContact] = useEnableContactMutation();
    const history = useHistory();
    const REFETCH_DELAY_MS = 500;
    const [pageSize, setPageSize] = useState<number>(25)
    const [filter, setFilter] = useState<string>()
    const [filterField, setFilterField] = useState<string>()
    const [sortBy, setSortBy] = useState<string>()
    const [sortDirection, setSortDirection] = useState<string>()
    const [page, setPage] = React.useState(1);
    const contacts = useGetContactsQuery({query: {query, conditions: {page: page, pageSize: pageSize, sortBy: sortBy, sortDirection: sortDirection, searchBy: filterField, searchTerm: filter}}, includeCompanyDetails: true, disabled: showDisabled})
    // Derived below the query rather than beside contactPanel, which is declared before it.
    const selected = contactPanel.isOpen
        ? contacts.data?.items?.find((c: IContact) => c.id === contactPanel.instanceId)
        : undefined;

    if (extensions.isLoading) {
        return <div>Loading...</div>
    }

    const getCustomColumns = () => {
        const columnDetails: IColumnDetails[] = [
            {
                identifier: 'Details',
                label: '',
                type: '',
                index: 0,
                component:() => <IconButton iconProps={{iconName: 'Edit'}}/>,
                onClick: (contact: IContact) => contactPanel.open(contact.id),
                required: true,
                effectiveWidth: 50,
            },
            {
                identifier: 'enabled',
                label: 'Enabled',
                type: '',
                index: 1,
                component:(contact) => <DisableEnableButton disabled={contact.disabled} id={contact.id} onDisable={() => disableContact(contact.id)} onEnable={() => enableContact(contact.id)}/>,
                required: true,
                effectiveWidth: 50
            },
            {
                identifier: 'companyName.keyword',
                label: 'Company',
                type: 'Text',
                sortable: true,
                filterable: true,
                minimumWidth: 150,
                filterField: 'company.keyword',
                customFilterComponent: (currentValue, onChange) => <CompanyFilterMenu selected={currentValue} onSelect={onChange} />,
                component: (contact: IContact) => {
                    if (!contact.company) return <span>-</span>;
                    return (
                        <Link onClick={() => setSelectedCompany(contact.company)}>
                            {contact.companyName ?? contact.company}
                        </Link>
                    );
                },
            },
            {
                identifier: 'storefront.keyword',
                label: 'Storefront',
                type: 'Text',
                sortable: true,
                filterable: true,
                minimumWidth: 120,
                component: (contact: IContact) => {
                    if (!contact.storefront) return <span>-</span>;
                    const storefront = storefronts.data?.items?.find(a => a.id === contact.storefront);
                    return (
                        <Link onClick={() => history.push(`/settings/storefront/storefronts/${contact.storefront}/dashboard`)}>
                            {storefront?.name ?? contact.storefront}
                        </Link>
                    );
                },
                customFilterOptions: () => storefronts.data?.items?.map(a => ({key: a.id ?? '', text: a.name ?? ''})),
                tooltip: 'Sort on Storefront is not alphabetical',
            }
        ];
        return columnDetails
    }

    const refetch = async() => {
        setIsLoading(true);
        await new Promise(f => setTimeout(f, REFETCH_DELAY_MS));
        contacts.refetch();
        setIsLoading(false);
    }

    const importExtensions = extensions.data?.filter(e => e.extensionType === EXTENSION_TYPE_CUSTOMERIMPORT);

    const additionalCommands: ICommandBarItemProps[] = [
        {
            key: 'new',
            text: 'Add New Contact',
            iconProps: { iconName: 'Add' },
            onClick: () => {setNewContact(true);},
        },
        {
            key: 'import',
            text: 'Import',
            iconProps: { iconName: 'Import' },
            subMenuProps: {
                items: [
                    ...(importExtensions?.map(e => ({
                        key: e.id,
                        text: e.name,
                        onClick: () => setSelectedImportExtension(e),
                    })) ?? []),
                    {
                        key: 'fromCsv',
                        text: 'From CSV',
                        iconProps: { iconName: 'ExcelDocument' },
                        onClick: () => setShowCsvImport(true),
                    },
                ]
            }
        },
        {
            key: 'includeDisabled',
            onRender: () => (
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                    <Toggle
                        label="Include Disabled"
                        inlineLabel
                        checked={showDisabled}
                        styles={{ root: { marginBottom: 0 } }}
                        onChange={(ev, checked) => {
                            setShowDisabled(checked ?? showDisabled);
                            setPage(1);
                        }}
                    />
                </div>
            ),
        },
    ]

    const additionalFarCommands: ICommandBarItemProps[] = [
        {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            iconOnly: true,
            onClick: () => {contacts.refetch()},
        }
    ]

    return (
        <div>
            <GridComposer
                data={contacts.data ?? {}}
                page={page}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                loading={isLoading || contacts.isFetching}
                selectionMode={GridSelectionMode.none}
                columnSetName={'Contact'}
                filter={filter}
                filterField={filterField}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={(column, direction) => {
                    setSortBy(column)
                    setSortDirection(direction)
                }}
                onFilter={(filter, column) => {
                    setFilter(filter)
                    setFilterField(column)
                }}
                overrideColumns={getCustomColumns()}
                canDragColumns={true}
                canResizeColumns={true}
                canCustomizeColumns={true}
                canChangePageSize={true}
                canCreateCsv={false}
                additionalCommands={additionalCommands}
                additionalFarCommands={additionalFarCommands}
                getItemId={(contact: IContact) => contact.id ?? ''}
            />
            <ContactDetailsModal
                isOpen={(contactPanel.isOpen && selected !== undefined) || newContact}
                onDismiss={(saved: boolean) => {
                    if (saved) {
                        refetch();
                    }
                    contactPanel.close();
                    setNewContact(false);
                }}
                contact={selected}
            />
            <CompanyDetailsModal
                company={selectedCompany}
                isOpen={selectedCompany !== undefined}
                onClose={() => setSelectedCompany(undefined)}
            />
            {selectedImportExtension && <ContactImportPanel
                extension={selectedImportExtension}
                onImport={() => contacts.refetch()}
                onClose={() => setSelectedImportExtension(undefined)}
            />}
            <CsvImportPanel
                importType='contact'
                isOpen={showCsvImport}
                onClose={() => setShowCsvImport(false)}
                onImported={() => contacts.refetch()}
            />
        </div>
    );
}

export default ContactsManagementDisplay;
